import { useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import {
  EmployeeForSelectFragment,
  SurveysTrainingClassFragment,
  TrainingSurveySendSurveyDocument,
  TrainingSurveysTrainingClassesDocument,
} from 'generated/resources/graphql'

import Callout from 'modules/core/components/Callout'
import Modal from 'modules/core/components/Modal'
import SelectField from 'modules/core/components/SelectField'
import Selector from 'modules/core/components/Selector'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EmployeeSelector } from 'modules/employees/components/EmployeeSelector'
import Loading from 'modules/layout/components/Page/Loading'

const SendSurveyModal = () => {
  const navigate = useNavigate()
  const { id, formId } = useParams<{ id: string; formId: string }>()

  const { navigation } = useNavigation()

  const [currentSelectedOptions, setCurrentSelectedOptions] = useState<number[]>([])
  const [selectedOption, setselectedOption] = useState<string>('1')
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeForSelectFragment | null>()

  const { data, loading: loadingTrainingGroups } = useQuery(
    TrainingSurveysTrainingClassesDocument,
    {
      variables: {
        formId: formId ?? '',
        trainingId: Number(id),
      },
    }
  )
  const [sendSurvey, { loading: sendingSurvey }] = useMutation(TrainingSurveySendSurveyDocument, {
    extractorFn: (mutations) => mutations.formsMutations.sendFormFormsForm,
  })
  const isEffecttivenessSurvey = data?.forms?.form?.formType === 'effectiveness'
  const isAnonymousSurvey = data?.forms?.form?.anonymous

  const disabled =
    currentSelectedOptions.length === 0 ||
    sendingSurvey ||
    (selectedOption == '2' && !selectedEmployee)
  const handledSubmit = async () => {
    if (!formId) {
      return Promise.reject('')
    }

    const response = await sendSurvey({
      variables: {
        id: formId,
        resourceIds: currentSelectedOptions.map((option) => option.toString()),
        respondentId: selectedEmployee?.access.id.toString() ?? null,
      },
    })

    const handledResponse = handleResult(response, () => {
      app.setFlash({ type: 'wrong', message: i18n.t('trainings.surveys.create.send_survey.error') })
    })
    if (handledResponse.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('trainings.surveys.create.send_survey.success'),
      })
    }
    navigate(onCloseRoute)
  }
  const options = data?.trainings.trainingClassesConnection.nodes ?? []
  const emptyGroups = options.length === 0
  const isFormSentToAllGroups = (trainingClasses: SurveysTrainingClassFragment[]) => {
    return trainingClasses.every((trainingClass) =>
      trainingClass.materializedFormsConnection.nodes.some(
        (materializedForm) => materializedForm.form?.id === formId
      )
    )
  }
  const isSent = formId && isFormSentToAllGroups(options)
  const noGroupMessage = i18n.t('trainings.surveys.create.send_survey.no_group_message')
  const moreInfoMessage = i18n.t('trainings.surveys.create.send_survey.more_info_message')
  const managersOptions = [
    {
      value: 1,
      label: i18n.t('trainings.surveys.create.send_survey.manager'),
    },
    {
      value: 2,
      label: i18n.t('trainings.surveys.create.send_survey.manual_selection'),
    },
  ]

  const onCloseRoute =
    navigation.trainings.list.details_revamp.trainingSurveyDetail.questions.fullPath({
      id: Number(id),
      formId: Number(formId),
    })

  const description = isEffecttivenessSurvey
    ? i18n.t('trainings.surveys.create.send_survey.description_effectiveness')
    : i18n.t('trainings.surveys.create.send_survey.description_satisfaction')

  const getDescriptionMessage = () => {
    if (emptyGroups) return noGroupMessage
    if (isSent) return i18n.t('trainings.surveys.create.send_survey.sent_to_all_groups')
    if (isAnonymousSurvey)
      return i18n.t('trainings.surveys.create.send_survey.more_info_message_anonymous')
    return moreInfoMessage
  }

  if (!id || !formId) return <Navigate to={onCloseRoute} />
  if (loadingTrainingGroups) return <></>
  return (
    <Modal
      title={i18n.t('trainings.surveys.create.send_survey.title')}
      description={description}
      illustration="document-edit"
      onClose={() => navigate(onCloseRoute)}
      onSubmit={() => handledSubmit()}
      footer={
        <Box flexDirection="row" gap="s8">
          <Button.Router to={onCloseRoute} type="secondary">
            {i18n.t('common.cancel')}
          </Button.Router>
          <Button submit disabled={disabled}>
            {i18n.t('common.send')}
          </Button>
        </Box>
      }
    >
      {sendingSurvey ? (
        <Loading />
      ) : (
        <Box gap="s24">
          <Box gap="s4">
            <Text.Normal weight="medium" size="200" color="grey1000">
              {i18n.t('trainings.surveys.create.send_survey.groups_title')}
            </Text.Normal>
            <Selector.Popover
              isDisabled={options.length === 0}
              hasSearch
              placeholder={i18n.t('common.select')}
              searchPlaceholder=""
              value={currentSelectedOptions}
              blankLabel=""
              // eslint-disable-next-line no-type-assertion/no-type-assertion
              onChange={(selection) => setCurrentSelectedOptions(selection as number[])}
              isMultiSelect
            >
              {options.map((option) => {
                const materializedFormsIds = option.materializedFormsConnection.nodes.map(
                  (op) => op.form?.id
                )
                return (
                  <Selector.Option
                    value={option.id}
                    label={option.name}
                    key={option.id}
                    searchValue={option.name}
                    isDisabled={materializedFormsIds.includes(formId)}
                  >
                    {option.name}
                  </Selector.Option>
                )
              })}
            </Selector.Popover>
          </Box>
          {isEffecttivenessSurvey ? (
            <Box gap="s16">
              <Text.Normal weight="medium" size="200" color="grey1000">
                {i18n.t('trainings.surveys.create.send_survey.managers_selector_title')}
              </Text.Normal>
              <SelectField
                options={managersOptions}
                onChange={(selection) => setselectedOption(selection)}
              />
              {selectedOption == '2' && (
                <EmployeeSelector
                  value={selectedEmployee}
                  onSelect={(employee) => setSelectedEmployee(employee)}
                  usingAccessIds
                />
              )}
            </Box>
          ) : (
            <Callout
              title={
                emptyGroups
                  ? i18n.t('trainings.surveys.create.send_survey.empty_groups')
                  : undefined
              }
              type={emptyGroups || isSent ? 'warning' : 'info'}
              description={getDescriptionMessage()}
              hideIcon
              action={
                emptyGroups ? (
                  <Button.Router
                    title={i18n.t('trainings.surveys.create.send_survey.create_group')}
                    to={navigation.trainings.list.details_revamp.view.trainingClasses.tab.newClass.fullPath(
                      {
                        id: Number(id),
                      }
                    )}
                    type="secondary"
                  >
                    {i18n.t('trainings.surveys.create.send_survey.create_group')}
                  </Button.Router>
                ) : isSent ? (
                  <Button.Router
                    title={i18n.t('trainings.surveys.create.send_survey.create_group')}
                    to={navigation.trainings.list.details_revamp.view.trainingClasses.tab.newClass.fullPath(
                      {
                        id: Number(id),
                      }
                    )}
                    type="secondary"
                  >
                    {i18n.t('trainings.surveys.create.send_survey.create_group')}
                  </Button.Router>
                ) : (
                  <></>
                )
              }
            />
          )}
        </Box>
      )}
    </Modal>
  )
}

export default SendSurveyModal
